import { Clock3, ExternalLink, Loader2, MapPin, User } from 'lucide-react'
import { useLanguage } from '../hooks/useLanguage'
import type { CalendarEvent } from '../types/calendar'
import { EventLabelBadge } from './EventLabelBadge'

const SOURCE_STYLES: Record<string, { bg: string; text: string }> = {
  blue: { bg: 'bg-blue-50 dark:bg-blue-950/40', text: 'text-blue-700 dark:text-blue-300' },
  purple: { bg: 'bg-purple-50 dark:bg-purple-950/40', text: 'text-purple-700 dark:text-purple-300' },
  indigo: { bg: 'bg-indigo-50 dark:bg-indigo-950/40', text: 'text-indigo-700 dark:text-indigo-300' },
  green: { bg: 'bg-emerald-50 dark:bg-emerald-950/40', text: 'text-emerald-700 dark:text-emerald-300' },
  teal: { bg: 'bg-teal-50 dark:bg-teal-950/40', text: 'text-teal-700 dark:text-teal-300' },
  orange: { bg: 'bg-orange-50 dark:bg-orange-950/40', text: 'text-orange-700 dark:text-orange-300' },
  gray: { bg: 'bg-gray-100 dark:bg-gray-800', text: 'text-gray-700 dark:text-gray-300' },
}

interface CalendarEventCardProps {
  event: CalendarEvent
  isFavorite?: boolean
  favoritePending?: boolean
  onToggleFavorite?: (event: CalendarEvent) => void
}

export function CalendarEventCard({ event, isFavorite = false, favoritePending = false, onToggleFavorite }: CalendarEventCardProps) {
  const { t } = useLanguage()
  const style = SOURCE_STYLES[event.source_color] || SOURCE_STYLES.gray

  return (
    <article className="rounded-xl border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-900">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="mb-1.5 flex flex-wrap items-center gap-1.5">
            <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ${style.bg} ${style.text}`}>
              {event.source_label}
            </span>
            <EventLabelBadge eventLabel={event.event_label} />
            {event.category && (
              <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-600 dark:bg-gray-800 dark:text-gray-400">
                {event.category}
              </span>
            )}
            {event.price && (
              <span className="rounded-full bg-green-50 px-2 py-0.5 text-[10px] font-semibold text-green-700 dark:bg-green-950/40 dark:text-green-300">
                {event.price}
              </span>
            )}
          </div>

          <h4 className="text-sm font-semibold leading-snug text-gray-900 dark:text-white">
            {event.title}
          </h4>

          <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
            {event.time && (
              <span className="flex items-center gap-1">
                <Clock3 className="h-3 w-3" />
                {event.time}
              </span>
            )}
            {event.location && (
              <span className="flex items-center gap-1">
                <MapPin className="h-3 w-3" />
                <span className="line-clamp-1">{event.location}</span>
              </span>
            )}
            {event.organizer && (
              <span className="flex items-center gap-1">
                <User className="h-3 w-3" />
                <span className="line-clamp-1">{event.organizer}</span>
              </span>
            )}
          </div>
        </div>

        {(event.detail_url || onToggleFavorite) && (
          <div className="flex shrink-0 self-stretch flex-col items-end justify-between gap-2">
            {event.detail_url ? (
              <a
                href={event.detail_url}
                target="_blank"
                rel="noopener noreferrer"
                className="rounded-lg border border-gray-200 p-1.5 text-gray-400 transition hover:border-blue-200 hover:text-blue-600 dark:border-gray-700 dark:hover:border-blue-800 dark:hover:text-blue-400"
                aria-label={t('查看详情', 'View details')}
              >
                <ExternalLink className="h-3.5 w-3.5" />
              </a>
            ) : <span />}
            {onToggleFavorite && (
              <button
                type="button"
                onClick={() => onToggleFavorite(event)}
                disabled={favoritePending}
                aria-pressed={isFavorite}
                className={`inline-flex min-h-6 items-center gap-0.5 whitespace-nowrap rounded-md px-2 py-1 text-[10px] font-semibold leading-none transition disabled:opacity-60 ${isFavorite ? 'bg-amber-100 text-amber-800 dark:bg-amber-950/50 dark:text-amber-300' : 'bg-gray-100 text-gray-600 hover:bg-amber-50 hover:text-amber-700 dark:bg-gray-800 dark:text-gray-300'}`}
              >
                {favoritePending ? <Loader2 className="h-3 w-3 animate-spin" /> : <span aria-hidden="true">★</span>}
                {isFavorite ? t('已收藏', 'Saved') : t('收藏', 'Save')}
              </button>
            )}
          </div>
        )}
      </div>
    </article>
  )
}
